import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '../features/auth/AuthContext';
import { useStudentCourseId } from '../features/courses/useStudentCourseId';
import { useInstructorCourseId } from '../features/instructor/useInstructorCourseId';
import { getActiveChallenge } from '../features/leaderboard/leaderboardApi';
import type { ActiveChallenge } from '../features/leaderboard/leaderboardApi';
import './MenuShell.css';

interface MenuShellProps {
  courseId: string;
}

function resolveChallengeLink(
  activeChallenge: ActiveChallenge | undefined,
  role: string | null,
  courseId: string,
): string {
  if (role === 'INSTRUCTOR') {
    return courseId ? `/courses/${courseId}/leaderboard` : '/instructor/challenge';
  }
  if (activeChallenge?.active) {
    return `/courses/${activeChallenge.courseId}/leaderboard`;
  }
  return '/exercise/first';
}

function resolveJoinClassLink(role: string | null, courseId: string): string {
  return role === 'INSTRUCTOR' ? '/instructor/roster' : `/courses/${courseId}`;
}

function MemberLinks({
  isInstructor,
  isAdmin,
  courseId,
}: {
  isInstructor: boolean;
  isAdmin: boolean;
  courseId: string;
}) {
  const { t } = useTranslation();
  const isStudent = !isInstructor && !isAdmin;
  const studentCourseQuery = useStudentCourseId(courseId, isStudent);
  return (
    <>
      <Link className="menu-shell__link" to="/courses">
        $ {t('menu.myCourses')}
      </Link>
      {isStudent && studentCourseQuery.courseId && (
        <Link className="menu-shell__link" to={`/courses/${studentCourseQuery.courseId}/progress`}>
          $ {t('menu.progress')}
        </Link>
      )}
      <Link className="menu-shell__link" to="/profile">
        $ {t('menu.profile')}
      </Link>
      {isInstructor && (
        <Link className="menu-shell__link" to="/instructor">
          $ {t('menu.instructor')}
        </Link>
      )}
      {isAdmin && (
        <Link className="menu-shell__link" to="/admin/import">
          $ {t('menu.adminImport')}
        </Link>
      )}
    </>
  );
}

function GuestLinks() {
  const { t } = useTranslation();
  return (
    <>
      <Link className="menu-shell__link" to="/login">
        $ {t('menu.login')}
      </Link>
      <Link className="menu-shell__link" to="/register">
        $ {t('menu.register')}
      </Link>
    </>
  );
}

export default function MenuShell({ courseId }: MenuShellProps) {
  const { t } = useTranslation();
  const { token, role, logout } = useAuth();
  const isAuthenticated = Boolean(token);
  const instructorCourseQuery = useInstructorCourseId(role === 'INSTRUCTOR');

  const activeChallengeQuery = useQuery({
    queryKey: ['nav', 'active-challenge'],
    queryFn: getActiveChallenge,
    enabled: isAuthenticated,
    refetchInterval: 3000,
  });
  const activeChallenge = activeChallengeQuery.data;
  const instructorCourseId = instructorCourseQuery.courseId ?? courseId;

  const challengeLinkTo = resolveChallengeLink(activeChallenge, role, instructorCourseId);
  const joinClassLinkTo = resolveJoinClassLink(role, courseId);
  const isSelectedClassChallengeActive =
    activeChallenge?.active &&
    (role !== 'INSTRUCTOR' || activeChallenge.courseId === instructorCourseId);

  return (
    <nav className="menu-shell" aria-label={t('menu.navigation')}>
      <Link className="menu-shell__brand" to="/">
        SHELL CHALLENGE
      </Link>
      <div className="menu-shell__links">
        <Link className="menu-shell__link" to={challengeLinkTo}>
          $ {t('menu.challenge')}
          {isSelectedClassChallengeActive && <span className="menu-shell__live-dot" aria-hidden="true" />}
        </Link>
        <span className="menu-shell__visually-hidden" aria-live="polite">
          {isSelectedClassChallengeActive ? t('menu.challengeLiveAnnouncement') : ''}
        </span>
        <Link className="menu-shell__link" to={joinClassLinkTo}>
          $ {t('menu.joinClass')}
        </Link>
        {isAuthenticated ? (
          <MemberLinks isInstructor={role === 'INSTRUCTOR'} isAdmin={role === 'ADMIN'} courseId={courseId} />
        ) : (
          <GuestLinks />
        )}
      </div>
      {isAuthenticated && (
        <div className="menu-shell__session">
          <span className="menu-shell__role">{role}</span>
          <button type="button" className="menu-shell__logout" onClick={logout}>
            {t('menu.logout')}
          </button>
        </div>
      )}
    </nav>
  );
}
